import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';

const SterileLocations = ({ itemMaster = [], supplies = [], instruments = [], onUpdateData }) => {
  const [activeMap, setActiveMap] = useState('map1'); // 'map1' or 'map2'
  const [selectedCabinet, setSelectedCabinet] = useState(null); // { map: 'map1', level: 1|2|3, number: 1..14 } or { map: 'map2', section: 'W'|'M'|'P1'|'P2'|'G1', number: number, level: number }
  const [searchQuery, setSearchQuery] = useState('');
  const [editItem, setEditItem] = useState(null); // Item being edited in modal
  const [editCab, setEditCab] = useState('');
  const [editShelf, setEditShelf] = useState('');
  const [editRow, setEditRow] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [movingItem, setMovingItem] = useState(null); // Item in quick placement mode

  // Helper: Normalize values for location matching
  const parseNum = (str) => {
    if (!str) return null;
    const match = str.toString().match(/\d+/);
    return match ? parseInt(match[0], 10) : null;
  };

  // Check if item belongs to a specific cabinet (1-14) and shelf level (1, 2, or 3)
  const isItemInLocation = (item, level, cabNum) => {
    // We display items from all categories if they have storage coordinates
    const cabStr = (item.cabinet || '').toString().trim().toLowerCase();
    const shfStr = (item.shelf || '').toString().trim().toLowerCase();
    const rowStr = (item.row || '').toString().trim().toLowerCase();

    if (!cabStr && !shfStr && !rowStr) return false;

    // 1. Match Cabinet Number (1..14)
    let matchedCabNum = parseNum(cabStr);

    // If cabinet contains formats like "1-5" (level 1, cab 5) or "2-12" (level 2, cab 12) or "3-5"
    if (cabStr.includes('-') || cabStr.includes('.')) {
      const parts = cabStr.split(/[-.]/);
      if (parts.length >= 2) {
        const lvlPart = parseInt(parts[0].replace(/\D/g, ''), 10);
        const cabPart = parseInt(parts[1].replace(/\D/g, ''), 10);
        if (lvlPart === level && cabPart === cabNum) return true;
      }
    }

    // Letter-based matching: "a5", "b12", or "c3" where A=1, B=2, C=3
    const levelLetter = level === 1 ? 'a' : (level === 2 ? 'b' : 'c');
    const isLetterMatch = cabStr.includes(levelLetter + cabNum) || cabStr.includes(cabNum + levelLetter);

    if (matchedCabNum !== cabNum && !isLetterMatch) {
      return false;
    }

    // 2. Match Level (1 = Row A, 2 = Row B, 3 = Row C)
    // Check cabinet letters or prefixes first
    if (cabStr.includes('a') || cabStr.startsWith('1-') || cabStr.includes('ชั้น 1') || cabStr.includes('l1') || cabStr.includes('level 1')) {
      return level === 1;
    }
    if (cabStr.includes('b') || cabStr.startsWith('2-') || cabStr.includes('ชั้น 2') || cabStr.includes('l2') || cabStr.includes('level 2')) {
      return level === 2;
    }
    if (cabStr.includes('c') || cabStr.startsWith('3-') || cabStr.includes('เคาน์เตอร์') || cabStr.includes('counter') || cabStr.includes('l3') || cabStr.includes('level 3')) {
      return level === 3;
    }

    // Check shelf field
    const shelfNum = parseNum(shfStr);
    if (shelfNum === 1 || shelfNum === 2 || shelfNum === 3) {
      return shelfNum === level;
    }
    if (shfStr.includes('c') || shfStr.includes('เคาน์เตอร์') || shfStr.includes('counter') || shfStr.includes('ชั้น 3') || shfStr.includes('ชั้นที่ 3')) {
      return level === 3;
    }
    if (shfStr.includes('b') || shfStr.includes('upper') || shfStr.includes('บน') || shfStr.includes('ชั้น 2') || shfStr.includes('ชั้นที่ 2')) {
      return level === 2;
    }
    if (shfStr.includes('a') || shfStr.includes('lower') || shfStr.includes('ล่าง') || shfStr.includes('ชั้น 1') || shfStr.includes('ชั้นที่ 1')) {
      return level === 1;
    }

    // Check row field
    const rowNum = parseNum(rowStr);
    if (rowNum === 1 || rowNum === 2 || rowNum === 3) {
      return rowNum === level;
    }
    if (rowStr.includes('a')) return level === 1;
    if (rowStr.includes('b')) return level === 2;
    if (rowStr.includes('c')) return level === 3;

    // Fallback: If cabinet has number but no explicit level, default to Level 1 (Row A)
    return level === 1;
  };

  // Check if item belongs to Map 2: W1..W10, M1..M2, P1, P2, G1
  const isItemInMap2Location = (item, section, colNum, levelNum) => {
    const cabStr = (item.cabinet || '').toString().trim().toLowerCase();
    const shfStr = (item.shelf || '').toString().trim().toLowerCase();
    const rowStr = (item.row || '').toString().trim().toLowerCase();

    if (!cabStr && !shfStr && !rowStr) return false;

    // 1. W (Wall Cabinet 10 cols, 5 rows)
    if (section === 'W') {
      const isW = cabStr.includes('w') || cabStr.includes('แขวน') || cabStr.includes('ติดผนัง');
      if (!isW) return false;
      const cabNum = parseNum(cabStr);
      if (cabNum !== colNum) return false;

      const parsedLevel = parseNum(shfStr) || parseNum(rowStr);
      return parsedLevel === levelNum;
    }

    // 2. M (Metal Trolley: M1=Left, M2=Right, 3 rows)
    if (section === 'M') {
      const isM = cabStr.includes('m') || cabStr.includes('รถเข็น') || cabStr.includes('สแตนเลส');
      if (!isM) return false;
      
      let cabNum = parseNum(cabStr);
      if (!cabNum) {
        if (cabStr.includes('ซ้าย')) cabNum = 1;
        if (cabStr.includes('ขวา')) cabNum = 2;
      }
      if (cabNum !== colNum) return false;

      const parsedLevel = parseNum(shfStr) || parseNum(rowStr);
      return parsedLevel === levelNum;
    }

    // 3. P1 (Pink Drawer 1 - Left, 5 rows)
    if (section === 'P1') {
      const isP1 = cabStr.includes('p1') || (cabStr.includes('ชมพู') && (cabStr.includes('1') || cabStr.includes('ซ้าย'))) || (cabStr.includes('pink') && cabStr.includes('1'));
      if (!isP1) return false;

      const parsedLevel = parseNum(shfStr) || parseNum(rowStr);
      return parsedLevel === levelNum;
    }

    // 4. P2 (Pink Drawer 2 - Right, 5 rows)
    if (section === 'P2') {
      const isP2 = cabStr.includes('p2') || (cabStr.includes('ชมพู') && (cabStr.includes('2') || cabStr.includes('ขวา'))) || (cabStr.includes('pink') && cabStr.includes('2'));
      if (!isP2) return false;

      const parsedLevel = parseNum(shfStr) || parseNum(rowStr);
      return parsedLevel === levelNum;
    }

    // 5. G1 (Grey Drawer - on top of P2, 4 rows)
    if (section === 'G1') {
      const isG1 = cabStr.includes('g') || cabStr.includes('เทา') || cabStr.includes('จัดระเบียบ');
      if (!isG1) return false;

      const parsedLevel = parseNum(shfStr) || parseNum(rowStr);
      return parsedLevel === levelNum;
    }

    return false;
  };

  // Get all items registered in a specific cabinet (Map 1)
  const getCabinetItemsList = (level, cabNum) => {
    return itemMaster.filter(item => isItemInLocation(item, level, cabNum));
  };

  // Get all items in specific Map 2 location
  const getMap2CabinetItemsList = (section, colNum, levelNum) => {
    return itemMaster.filter(item => isItemInMap2Location(item, section, colNum, levelNum));
  };

  // Get stock amount in Sterile Room (ห้อง sterile)
  const getSterileStock = (itemId) => {
    const sup = supplies.find(s => s.master_id === itemId && s.warehouse_name.includes("ห้อง sterile"));
    return sup ? (sup.item_amount || 0) : 0;
  };

  // Get items matching query
  const searchFilter = (item) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase().trim();
    return (
      (item.item_name || '').toLowerCase().includes(query) ||
      (item.aliases || '').toLowerCase().includes(query) ||
      (item.category || '').toLowerCase().includes(query) ||
      (item.cabinet || '').toLowerCase().includes(query) ||
      (item.shelf || '').toLowerCase().includes(query) ||
      (item.row || '').toLowerCase().includes(query)
    );
  };

  // Check if cabinet has search hits (Map 1)
  const cabinetHasSearchHit = (level, cabNum) => {
    if (!searchQuery) return false;
    const items = getCabinetItemsList(level, cabNum);
    return items.some(searchFilter);
  };

  // Check if Map 2 location has search hits
  const cabinetHasSearchHitMap2 = (section, colNum, levelNum) => {
    if (!searchQuery) return false;
    const items = getMap2CabinetItemsList(section, colNum, levelNum);
    return items.some(searchFilter);
  };

  // Handle location update
  const handleSaveLocation = async (e) => {
    if (e) e.preventDefault();
    if (!editItem) return;

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('item_master')
        .update({
          cabinet: editCab.trim(),
          shelf: editShelf.trim(),
          row: editRow.trim(),
          last_updated: new Date().toISOString()
        })
        .eq('id', editItem.id);

      if (error) throw error;

      // Refresh parent state
      if (onUpdateData) await onUpdateData();

      // Clear states
      setEditItem(null);
    } catch (err) {
      alert("เกิดข้อผิดพลาดในการอัปเดตตำแหน่ง: " + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Quick Move click on cabinet block for Map 1
  const handleQuickMoveAssign = async (level, cabNum) => {
    if (!movingItem) return;
    
    const targetCab = level === 1 ? `ตู้ A${cabNum}` : (level === 2 ? `ตู้ B${cabNum}` : `ตู้ C${cabNum}`);
    const targetShelf = level === 3 ? 'ท็อปเคาน์เตอร์' : `ชั้น ${level}`;
    const targetRow = movingItem.row || 'แถว 1';

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('item_master')
        .update({
          cabinet: targetCab,
          shelf: targetShelf,
          row: targetRow,
          last_updated: new Date().toISOString()
        })
        .eq('id', movingItem.id);

      if (error) throw error;

      // Refresh parent state
      if (onUpdateData) await onUpdateData();

      // Show success indicator and clear quick move mode
      setMovingItem(null);
      // Auto select this cabinet to view the placed item
      setSelectedCabinet({ map: 'map1', level, number: cabNum });
    } catch (err) {
      alert("เกิดข้อผิดพลาดในการย้ายตำแหน่งด่วน: " + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Quick Move click on cabinet block for Map 2
  const handleQuickMoveAssignMap2 = async (section, colNum, levelNum) => {
    if (!movingItem) return;
    
    let targetCab = '';
    let targetShelf = `ชั้น ${levelNum}`;
    let targetRow = '';

    if (section === 'W') {
      targetCab = `ตู้ W${colNum}`;
    } else if (section === 'M') {
      targetCab = `รถเข็นสแตนเลส M${colNum}`;
    } else if (section === 'P1') {
      targetCab = `ตู้ชมพู P1`;
    } else if (section === 'P2') {
      targetCab = `ตู้ชมพู P2`;
    } else if (section === 'G1') {
      targetCab = `ตู้เทา G1`;
    }

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('item_master')
        .update({
          cabinet: targetCab,
          shelf: targetShelf,
          row: targetRow,
          last_updated: new Date().toISOString()
        })
        .eq('id', movingItem.id);

      if (error) throw error;

      // Refresh parent state
      if (onUpdateData) await onUpdateData();

      // Show success indicator and clear quick move mode
      setMovingItem(null);
      // Auto select this cabinet to view the placed item
      setSelectedCabinet({ map: 'map2', section, number: colNum, level: levelNum });
    } catch (err) {
      alert("เกิดข้อผิดพลาดในการย้ายตำแหน่งด่วน: " + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Open edit modal for an item
  const openEditModal = (item) => {
    setEditItem(item);
    setEditCab(item.cabinet || '');
    setEditShelf(item.shelf || '');
    setEditRow(item.row || '');
  };

  // All durable items list (for mapping sidebar / unassigned items)
  const durableItems = itemMaster.filter(item => item.category === 'วัสดุคงทน');
  const unassignedItems = durableItems.filter(item => !item.cabinet && !item.shelf && !item.row);

  // Render a highly detailed graphical cabinet representation based on the real image
  const renderCabinet = (level, i) => {
    const itemsInCab = getCabinetItemsList(level, i);
    const matchedSearch = cabinetHasSearchHit(level, i);
    const isSelected = (selectedCabinet?.map === 'map1' || !selectedCabinet?.map) && selectedCabinet?.level === level && selectedCabinet?.number === i;
    const itemCount = itemsInCab.length;
    
    // Quick placement mode
    const isDropzone = !!movingItem;

    // Determine interactive highlight styles
    let highlightShadow = 'none';
    let highlightBorder = 'none';
    
    if (matchedSearch) {
      highlightBorder = '3px solid #f1c40f';
      highlightShadow = '0 0 15px rgba(241, 196, 15, 0.8)';
    } else if (isSelected) {
      highlightBorder = '3px solid #1abc9c';
      highlightShadow = '0 0 20px rgba(26, 188, 156, 0.9)';
    } else if (isDropzone) {
      highlightBorder = '2.5px dashed #3498db';
    }

    // CLICK handler
    const handleClick = () => {
      if (movingItem) {
        handleQuickMoveAssign(level, i);
      } else {
        setSelectedCabinet({ map: 'map1', level, number: i });
      }
    };

    const cabinetWidth = '82px';

    if (level === 2) {
      // ==================== UPPER GLASS CABINETS ====================
      // Height: 130px
      // Light wood borders, glass background, internal shelf line
      return (
        <div
          key={i}
          onClick={handleClick}
          style={{
            width: cabinetWidth,
            height: '130px',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.03) 100%)',
            border: highlightBorder !== 'none' ? highlightBorder : '4px solid #ebd5c0', // Wood frame color
            boxShadow: highlightShadow,
            borderRadius: '4px',
            position: 'relative',
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            padding: '6px',
            backdropFilter: 'blur(1px)',
            WebkitBackdropFilter: 'blur(1px)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-3px)';
            e.currentTarget.style.boxShadow = isSelected ? highlightShadow : '0 8px 16px rgba(0,0,0,0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = isSelected ? highlightShadow : 'none';
          }}
        >
          {/* Glass reflection overlay */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(115deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.18) 35%, rgba(255,255,255,0.0) 40%)',
            pointerEvents: 'none',
            zIndex: 2
          }} />

          {/* Internal Shelf Line */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '2px',
            right: '2px',
            height: '2px',
            background: 'rgba(235, 213, 192, 0.6)', // Thin wooden shelf
            zIndex: 1
          }} />

          {/* Cabinet number sticker */}
          <div style={{
            fontSize: '0.7em',
            fontWeight: 'bold',
            color: '#2d3748',
            background: 'rgba(255,255,255,0.85)',
            padding: '1px 4px',
            borderRadius: '3px',
            width: 'fit-content',
            border: '1px solid #cbd5e0',
            zIndex: 3
          }}>
            B{i}
          </div>

          {/* Render sterile packs inside */}
          {itemCount > 0 && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '15px',
              alignItems: 'center',
              width: '100%',
              zIndex: 1
            }}>
              {/* Upper shelf items */}
              <div style={{ display: 'flex', gap: '3px', height: '22px', alignItems: 'flex-end' }}>
                {Array.from({ length: Math.min(itemCount, 3) }).map((_, idx) => (
                  <div key={idx} style={{
                    width: '14px',
                    height: '10px',
                    background: '#9b59b6', // Purple wraps matching the image!
                    borderRadius: '2px',
                    border: '1px solid #7d4693',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
                  }} />
                ))}
              </div>

              {/* Lower shelf items */}
              {itemCount > 3 && (
                <div style={{ display: 'flex', gap: '3px', height: '22px', alignItems: 'flex-end' }}>
                  {Array.from({ length: Math.min(itemCount - 3, 3) }).map((_, idx) => (
                    <div key={idx} style={{
                      width: '14px',
                      height: '10px',
                      background: '#3498db', // Blue wrap
                      borderRadius: '2px',
                      border: '1px solid #2980b9',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
                    }} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Digital Badge for item count */}
          <div style={{
            alignSelf: 'flex-end',
            fontSize: '0.72em',
            fontWeight: 'bold',
            background: matchedSearch ? '#f1c40f' : (itemCount > 0 ? '#1abc9c' : 'rgba(255,255,255,0.15)'),
            color: itemCount > 0 ? '#fff' : 'rgba(255,255,255,0.4)',
            padding: '2px 6px',
            borderRadius: '10px',
            zIndex: 3
          }}>
            {itemCount > 0 ? `${itemCount} ชิ้น` : 'ว่าง'}
          </div>

          {/* Door Frame Handles */}
          <div style={{
            position: 'absolute',
            right: '4px',
            top: '35%',
            bottom: '35%',
            width: '3px',
            background: '#64748b',
            borderRadius: '2px',
            zIndex: 3
          }} />
        </div>
      );
    } else if (level === 3) {
      // ==================== MIDDLE COUNTERTOP ORGANIZERS ====================
      // Height: 70px
      // Styled as various plastic drawers, baskets, and organizers sitting on the countertop.
      let organizerBg = 'rgba(255,255,255,0.05)';
      let organizerBorder = '1px solid rgba(255,255,255,0.1)';
      let organizerContent = null;
      let labelText = `C${i}`;

      const isBlueDrawer = i === 1 || i === 2 || i === 3;
      const isPinkDrawer = i === 4 || i === 5;
      const isGreenBasket = i === 6 || i === 8;
      const isBlueBasket = i === 7 || i === 9;
      const isRedBasket = i === 10;
      const isYellowBasket = i === 11;
      const isTransparentBox = i === 12;

      if (isBlueDrawer) {
        organizerBg = 'linear-gradient(180deg, #dbeafe 0%, #93c5fd 100%)';
        organizerBorder = '1.5px solid #2563eb';
        organizerContent = (
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', justifyContent: 'space-between' }}>
            {[1, 2, 3, 4].map(idx => (
              <div key={idx} style={{ flex: 1, borderBottom: idx < 4 ? '1px solid #2563eb' : 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ width: '4px', height: '4px', background: '#ffffff', borderRadius: '50%' }} />
              </div>
            ))}
          </div>
        );
      } else if (isPinkDrawer) {
        organizerBg = 'linear-gradient(180deg, #fce7f3 0%, #fbcfe8 100%)';
        organizerBorder = '1.5px solid #db2777';
        organizerContent = (
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', justifyContent: 'space-between' }}>
            {[1, 2, 3, 4].map(idx => (
              <div key={idx} style={{ flex: 1, borderBottom: idx < 4 ? '1px solid #db2777' : 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ width: '4px', height: '4px', background: '#ffffff', borderRadius: '50%' }} />
              </div>
            ))}
          </div>
        );
      } else if (isGreenBasket) {
        organizerBg = 'rgba(46, 204, 113, 0.15)';
        organizerBorder = '2px solid #2ecc71';
        organizerContent = (
          <div style={{
            width: '100%',
            height: '100%',
            border: '1.5px dashed rgba(46, 204, 113, 0.4)',
            borderRadius: '4px',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {/* Mesh pattern simulation */}
            <div style={{ fontSize: '0.6em', color: '#2ecc71', opacity: 0.7 }}>🧺 ตะกร้าเขียว</div>
          </div>
        );
      } else if (isBlueBasket) {
        organizerBg = 'rgba(52, 152, 219, 0.15)';
        organizerBorder = '2px solid #3498db';
        organizerContent = (
          <div style={{
            width: '100%',
            height: '100%',
            border: '1.5px dashed rgba(52, 152, 219, 0.4)',
            borderRadius: '4px',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div style={{ fontSize: '0.6em', color: '#3498db', opacity: 0.7 }}>🧺 ตะกร้าฟ้า</div>
          </div>
        );
      } else if (isRedBasket) {
        organizerBg = 'rgba(231, 76, 60, 0.15)';
        organizerBorder = '2px solid #e74c3c';
        organizerContent = (
          <div style={{
            width: '100%',
            height: '100%',
            border: '1.5px dashed rgba(231, 76, 60, 0.4)',
            borderRadius: '4px',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div style={{ fontSize: '0.65em', color: '#e74c3c', opacity: 0.8, fontWeight: 'bold' }}>🔴 ตะกร้าแดง</div>
          </div>
        );
      } else if (isYellowBasket) {
        organizerBg = 'rgba(241, 196, 15, 0.15)';
        organizerBorder = '2px solid #f1c40f';
        organizerContent = (
          <div style={{
            width: '100%',
            height: '100%',
            border: '1.5px dashed rgba(241, 196, 15, 0.4)',
            borderRadius: '4px',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div style={{ fontSize: '0.65em', color: '#f1c40f', opacity: 0.8, fontWeight: 'bold' }}>🟡 ตะกร้าเหลือง</div>
          </div>
        );
      } else if (isTransparentBox) {
        organizerBg = 'rgba(255, 255, 255, 0.08)';
        organizerBorder = '1.5px solid #cbd5e1';
        organizerContent = (
          <div style={{ display: 'flex', gap: '2px', alignItems: 'flex-end', height: '100%', padding: '4px' }}>
            <div style={{ width: '8px', height: '100%', background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '1px' }} />
            <div style={{ width: '12px', height: '70%', background: 'rgba(155, 89, 182, 0.4)', border: '1px solid rgba(155, 89, 182, 0.6)', borderRadius: '1px' }} />
            <div style={{ width: '10px', height: '85%', background: 'rgba(52, 152, 219, 0.4)', border: '1px solid rgba(52, 152, 219, 0.6)', borderRadius: '1px' }} />
          </div>
        );
      } else {
        // Empty Workspace Bench space
        organizerBg = 'rgba(255,255,255,0.02)';
        organizerBorder = '1px dashed rgba(255,255,255,0.15)';
        organizerContent = (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'rgba(255,255,255,0.1)', fontSize: '0.55em' }}>
            พื้นที่โล่ง
          </div>
        );
      }

      return (
        <div
          key={i}
          onClick={handleClick}
          style={{
            width: cabinetWidth,
            height: '70px',
            background: organizerBg,
            border: highlightBorder !== 'none' ? highlightBorder : organizerBorder,
            boxShadow: highlightShadow,
            borderRadius: '6px',
            position: 'relative',
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            padding: '4px',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = isSelected ? highlightShadow : '0 5px 10px rgba(0,0,0,0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = isSelected ? highlightShadow : 'none';
          }}
        >
          {/* Label Tag */}
          <div style={{
            position: 'absolute',
            top: '3px',
            left: '3px',
            background: 'rgba(0,0,0,0.6)',
            color: '#fff',
            fontSize: '0.65em',
            fontWeight: 'bold',
            padding: '0 3px',
            borderRadius: '2px',
            zIndex: 4
          }}>
            {labelText}
          </div>

          <div style={{ flex: 1, height: '100%', width: '100%', marginTop: '10px' }}>
            {organizerContent}
          </div>

          {/* Digital Badge for item count */}
          {itemCount > 0 && (
            <div style={{
              position: 'absolute',
              bottom: '2px',
              right: '2px',
              fontSize: '0.65em',
              fontWeight: '800',
              background: matchedSearch ? '#f1c40f' : '#16a085',
              color: '#fff',
              padding: '0 4px',
              borderRadius: '6px',
              zIndex: 3
            }}>
              {itemCount}
            </div>
          )}
        </div>
      );
    } else {
      // ==================== LOWER WOOD CABINETS ====================
      // Height: 160px
      // Cabinet 5, 6, 10: Drawers (3 drawers)
      // Cabinet 8, 9: Open knee space (no wood background)
      // Others: Standard paneled wooden doors
      
      const isDrawer = i === 5 || i === 6 || i === 10;
      const isOpenSpace = i === 8 || i === 9;
      
      let cabBg = 'linear-gradient(180deg, #ecdcc8 0%, #dbcbba 100%)';
      let internalLayout = null;

      if (isOpenSpace) {
        cabBg = 'linear-gradient(180deg, #1e2530 0%, #0f131a 100%)';
        internalLayout = (
          <div style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'rgba(255,255,255,0.15)',
            fontSize: '0.7em',
            fontWeight: 'bold',
            borderTop: '2px solid #555'
          }}>
            ช่องโล่ง
          </div>
        );
      } else if (isDrawer) {
        // Drawers
        internalLayout = (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            height: '100%',
            width: '100%'
          }}>
            {[1, 2, 3].map(d => (
              <div key={d} style={{
                flex: 1,
                borderBottom: d < 3 ? '1.5px solid rgba(0,0,0,0.15)' : 'none',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {/* Horizontal Drawer Handle */}
                <div style={{
                  width: '35px',
                  height: '3px',
                  background: '#64748b',
                  borderRadius: '2px',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
                }} />
              </div>
            ))}
          </div>
        );
      } else {
        // Wooden Doors
        internalLayout = (
          <div style={{
            position: 'relative',
            width: '100%',
            height: '100%',
            borderLeft: '1px solid rgba(0,0,0,0.05)',
            borderRight: '1px solid rgba(0,0,0,0.05)'
          }}>
            {/* Center parting line */}
            <div style={{
              position: 'absolute',
              left: '50%',
              top: 0,
              bottom: 0,
              width: '1.5px',
              background: 'rgba(0,0,0,0.15)'
            }} />
            
            {/* Double handles */}
            <div style={{
              position: 'absolute',
              left: 'calc(50% - 6px)',
              top: '25%',
              width: '3px',
              height: '30px',
              background: '#64748b',
              borderRadius: '2px'
            }} />
            <div style={{
              position: 'absolute',
              right: 'calc(50% - 6px)',
              top: '25%',
              width: '3px',
              height: '30px',
              background: '#64748b',
              borderRadius: '2px'
            }} />
          </div>
        );
      }

      return (
        <div
          key={i}
          onClick={handleClick}
          style={{
            width: cabinetWidth,
            height: '160px',
            background: cabBg,
            border: highlightBorder !== 'none' ? highlightBorder : (isOpenSpace ? '2px solid #555' : '3px solid #dbcbba'),
            boxShadow: highlightShadow,
            borderRadius: '4px',
            position: 'relative',
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            padding: '5px',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-3px)';
            e.currentTarget.style.boxShadow = isSelected ? highlightShadow : '0 8px 16px rgba(0,0,0,0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = isSelected ? highlightShadow : 'none';
          }}
        >
          {/* Label Sticker - Like paper labels in actual picture */}
          <div style={{
            position: 'absolute',
            top: '8px',
            left: '8px',
            background: '#ffffff',
            color: '#1e293b',
            border: '1px solid #94a3b8',
            fontSize: '0.7em',
            fontWeight: 'bold',
            padding: '1px 4px',
            borderRadius: '2px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            zIndex: 4
          }}>
            A{i}
          </div>

          {/* Internal Layout (drawers, doors or open space) */}
          <div style={{ flex: 1, marginTop: '20px', width: '100%' }}>
            {internalLayout}
          </div>

          {/* Digital Badge for item count */}
          {!isOpenSpace && (
            <div style={{
              alignSelf: 'center',
              fontSize: '0.72em',
              fontWeight: '800',
              background: matchedSearch ? '#f1c40f' : (itemCount > 0 ? '#16a085' : 'rgba(0,0,0,0.1)'),
              color: itemCount > 0 ? '#fff' : 'rgba(255,255,255,0.4)',
              padding: '2px 6px',
              borderRadius: '8px',
              marginTop: '5px',
              textAlign: 'center',
              width: '80%',
              zIndex: 3
            }}>
              {itemCount > 0 ? `${itemCount} รายการ` : 'ว่าง'}
            </div>
          )}
        </div>
      );
    }
  };

  // Reset selected cabinet on search to help user find items across both maps
  useEffect(() => {
    if (searchQuery) {
      // Check Map 1
      for (let lvl of [2, 3, 1]) {
        for (let num = 1; num <= 14; num++) {
          if (cabinetHasSearchHit(lvl, num)) {
            setActiveMap('map1');
            setSelectedCabinet({ map: 'map1', level: lvl, number: num });
            return;
          }
        }
      }

      // Check Map 2: W (1..10, lvl 1..5)
      for (let num = 1; num <= 10; num++) {
        for (let lvl = 1; lvl <= 5; lvl++) {
          if (cabinetHasSearchHitMap2('W', num, lvl)) {
            setActiveMap('map2');
            setSelectedCabinet({ map: 'map2', section: 'W', number: num, level: lvl });
            return;
          }
        }
      }

      // Check Map 2: M (1..2, lvl 1..3)
      for (let num = 1; num <= 2; num++) {
        for (let lvl = 1; lvl <= 3; lvl++) {
          if (cabinetHasSearchHitMap2('M', num, lvl)) {
            setActiveMap('map2');
            setSelectedCabinet({ map: 'map2', section: 'M', number: num, level: lvl });
            return;
          }
        }
      }

      // Check Map 2: P1 (lvl 1..5)
      for (let lvl = 1; lvl <= 5; lvl++) {
        if (cabinetHasSearchHitMap2('P1', 1, lvl)) {
          setActiveMap('map2');
          setSelectedCabinet({ map: 'map2', section: 'P1', number: 1, level: lvl });
          return;
        }
      }

      // Check Map 2: P2 (lvl 1..5)
      for (let lvl = 1; lvl <= 5; lvl++) {
        if (cabinetHasSearchHitMap2('P2', 1, lvl)) {
          setActiveMap('map2');
          setSelectedCabinet({ map: 'map2', section: 'P2', number: 1, level: lvl });
          return;
        }
      }

      // Check Map 2: G1 (lvl 1..4)
      for (let lvl = 1; lvl <= 4; lvl++) {
        if (cabinetHasSearchHitMap2('G1', 1, lvl)) {
          setActiveMap('map2');
          setSelectedCabinet({ map: 'map2', section: 'G1', number: 1, level: lvl });
          return;
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const activeCabinetItems = selectedCabinet 
    ? (selectedCabinet.map === 'map2'
      ? getMap2CabinetItemsList(selectedCabinet.section, selectedCabinet.number, selectedCabinet.level).filter(searchFilter)
      : getCabinetItemsList(selectedCabinet.level, selectedCabinet.number).filter(searchFilter))
    : [];

  // ==================== MAP 2 HELPER RENDERERS ====================

  const renderMap2WallCabinetCell = (colNum, rowNum) => {
    const items = getMap2CabinetItemsList('W', colNum, rowNum);
    const itemCount = items.length;
    const matchedSearch = cabinetHasSearchHitMap2('W', colNum, rowNum);
    const isSelected = selectedCabinet?.map === 'map2' && selectedCabinet?.section === 'W' && selectedCabinet?.number === colNum && selectedCabinet?.level === rowNum;
    const isDropzone = !!movingItem;

    let highlightShadow = 'none';
    let highlightBorder = 'none';
    if (matchedSearch) {
      highlightBorder = '3px solid #f1c40f';
      highlightShadow = '0 0 15px rgba(241, 196, 15, 0.8)';
    } else if (isSelected) {
      highlightBorder = '3px solid #1abc9c';
      highlightShadow = '0 0 20px rgba(26, 188, 156, 0.9)';
    } else if (isDropzone) {
      highlightBorder = '2px dashed #3498db';
    }

    const handleClick = () => {
      if (movingItem) {
        handleQuickMoveAssignMap2('W', colNum, rowNum);
      } else {
        setSelectedCabinet({ map: 'map2', section: 'W', number: colNum, level: rowNum });
      }
    };

    return (
      <div
        key={colNum}
        onClick={handleClick}
        style={{
          flex: 1,
          height: '52px',
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.03) 100%)',
          border: highlightBorder !== 'none' ? highlightBorder : '1px solid rgba(235, 213, 192, 0.4)',
          boxShadow: highlightShadow,
          borderRadius: '3px',
          position: 'relative',
          cursor: 'pointer',
          transition: 'all 0.2s',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          padding: '4px',
          minWidth: '75px',
          backdropFilter: 'blur(1px)',
          WebkitBackdropFilter: 'blur(1px)'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.03)';
          e.currentTarget.style.boxShadow = isSelected ? highlightShadow : '0 4px 10px rgba(0,0,0,0.3)';
          e.currentTarget.style.zIndex = '10';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = isSelected ? highlightShadow : 'none';
          e.currentTarget.style.zIndex = '1';
        }}
      >
        {/* Reflection */}
        <div style={{
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'linear-gradient(115deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.12) 30%, rgba(255,255,255,0) 35%)',
          pointerEvents: 'none',
          zIndex: 2
        }} />

        {/* Sticker / coordinate text */}
        <div style={{
          fontSize: '0.62em',
          fontWeight: 'bold',
          color: '#2d3748',
          background: 'rgba(255,255,255,0.85)',
          padding: '0px 2px',
          borderRadius: '2px',
          width: 'fit-content',
          zIndex: 3
        }}>
          W{colNum}-{rowNum}
        </div>

        {/* Realistic plastic box inside */}
        {itemCount > 0 && (
          <div style={{
            position: 'absolute',
            top: '15px',
            left: '10px',
            right: '10px',
            bottom: '12px',
            background: 'rgba(255, 255, 255, 0.2)',
            border: '1.5px solid #3498db', // Blue outline matching lid in photo
            borderRadius: '3px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1,
            boxShadow: 'inset 0 1px 3px rgba(255,255,255,0.5)'
          }}>
            <div style={{ width: '80%', height: '3px', background: '#3498db', position: 'absolute', top: 0 }} /> {/* Lid */}
          </div>
        )}

        {/* Item badge count */}
        <div style={{
          alignSelf: 'flex-end',
          fontSize: '0.65em',
          fontWeight: 'bold',
          background: matchedSearch ? '#f1c40f' : (itemCount > 0 ? '#1abc9c' : 'rgba(255,255,255,0.1)'),
          color: itemCount > 0 ? '#fff' : 'rgba(255,255,255,0.3)',
          padding: '1px 4px',
          borderRadius: '6px',
          zIndex: 3
        }}>
          {itemCount > 0 ? `${itemCount}` : ''}
        </div>
      </div>
    );
  };

  const renderMetalTrolley = () => {
    return (
      <div style={{
        width: '260px',
        background: 'linear-gradient(135deg, #cbd5e1 0%, #94a3b8 50%, #64748b 100%)',
        border: '3px solid #475569',
        borderRadius: '8px',
        padding: '8px',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        boxShadow: '0 10px 20px rgba(0,0,0,0.3)',
        height: '240px',
        justifyContent: 'space-between'
      }}>
        {/* Blue Basket on top */}
        <div style={{
          position: 'absolute',
          top: '-18px',
          left: '20px',
          width: '100px',
          height: '14px',
          background: 'rgba(52, 152, 219, 0.85)',
          border: '2px solid #2980b9',
          borderRadius: '3px',
          fontSize: '0.5em',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 'bold',
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          zIndex: 10
        }}>
          🧺 ตะกร้าฟ้า
        </div>

        <div style={{
          textAlign: 'center',
          fontSize: '0.7em',
          fontWeight: 'bold',
          color: '#1e293b',
          background: 'rgba(255,255,255,0.7)',
          padding: '2px',
          borderRadius: '3px',
          marginBottom: '6px'
        }}>
          ⚙️ รถเข็นสแตนเลส (M1 / M2)
        </div>

        {/* 2 columns of drawers */}
        <div style={{ display: 'flex', gap: '6px', flex: 1 }}>
          {/* Left M1, Right M2 */}
          {[1, 2].map(col => {
            return (
              <div key={col} style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '5px' }}>
                {[1, 2, 3].map(row => {
                  const items = getMap2CabinetItemsList('M', col, row);
                  const itemCount = items.length;
                  const matchedSearch = cabinetHasSearchHitMap2('M', col, row);
                  const isSelected = selectedCabinet?.map === 'map2' && selectedCabinet?.section === 'M' && selectedCabinet?.number === col && selectedCabinet?.level === row;
                  const isDropzone = !!movingItem;

                  let highlightShadow = 'none';
                  let highlightBorder = 'none';
                  if (matchedSearch) {
                    highlightBorder = '2.5px solid #f1c40f';
                    highlightShadow = '0 0 10px rgba(241, 196, 15, 0.8)';
                  } else if (isSelected) {
                    highlightBorder = '2.5px solid #1abc9c';
                    highlightShadow = '0 0 15px rgba(26, 188, 156, 0.9)';
                  } else if (isDropzone) {
                    highlightBorder = '1.5px dashed #3498db';
                  }

                  const handleClick = () => {
                    if (movingItem) {
                      handleQuickMoveAssignMap2('M', col, row);
                    } else {
                      setSelectedCabinet({ map: 'map2', section: 'M', number: col, level: row });
                    }
                  };

                  return (
                    <div
                      key={row}
                      onClick={handleClick}
                      style={{
                        flex: 1,
                        background: 'linear-gradient(180deg, #cbd5e1 0%, #64748b 100%)',
                        border: highlightBorder !== 'none' ? highlightBorder : '1.5px solid #94a3b8',
                        boxShadow: highlightShadow || 'inset 0 1px 2px rgba(255,255,255,0.3)',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        padding: '4px',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.02)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    >
                      {/* Label Sticker */}
                      <div style={{
                        fontSize: '0.6em',
                        fontWeight: 'bold',
                        color: '#334155',
                        background: 'rgba(255,255,255,0.8)',
                        width: 'fit-content',
                        padding: '0px 2px',
                        borderRadius: '2px',
                        zIndex: 3
                      }}>
                        M{col === 1 ? 'ซ้าย' : 'ขวา'}-{row}
                      </div>

                      {/* Drawer pull handle */}
                      <div style={{
                        width: '35px',
                        height: '4px',
                        background: '#475569',
                        alignSelf: 'center',
                        borderRadius: '2px',
                        boxShadow: '0 1px 1px rgba(0,0,0,0.3)'
                      }} />

                      {/* Item count badge */}
                      <div style={{
                        alignSelf: 'flex-end',
                        fontSize: '0.62em',
                        fontWeight: '800',
                        background: matchedSearch ? '#f1c40f' : (itemCount > 0 ? '#1abc9c' : 'rgba(0,0,0,0.15)'),
                        color: itemCount > 0 ? '#fff' : 'rgba(255,255,255,0.4)',
                        padding: '0px 4px',
                        borderRadius: '6px',
                        zIndex: 3
                      }}>
                        {itemCount > 0 ? `${itemCount} ชิ้น` : 'ว่าง'}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* Wheels at the bottom */}
        <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: '6px' }}>
          <div style={{ width: '12px', height: '12px', background: '#334155', borderRadius: '50%', border: '2px solid #cbd5e1' }} />
          <div style={{ width: '12px', height: '12px', background: '#334155', borderRadius: '50%', border: '2px solid #cbd5e1' }} />
        </div>
      </div>
    );
  };

  const renderPinkCabinet = (sec) => {
    return (
      <div style={{
        width: '150px',
        background: 'linear-gradient(180deg, #fbcfe8 0%, #f472b6 100%)',
        border: '3px solid #db2777',
        borderRadius: '8px',
        padding: '6px',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 10px 20px rgba(0,0,0,0.3)',
        height: '240px',
        justifyContent: 'space-between',
        position: 'relative'
      }}>
        {sec === 'P1' && (
          <div style={{
            position: 'absolute',
            top: '-16px',
            left: '12px',
            width: '120px',
            height: '12px',
            background: 'rgba(255, 255, 255, 0.85)',
            border: '1px solid #aaa',
            borderRadius: '2px',
            fontSize: '0.48em',
            color: '#333',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 'bold',
            zIndex: 10
          }}>
            📦 กล่องเปล่า & ตะกร้าชมพู
          </div>
        )}
        
        <div style={{
          textAlign: 'center',
          fontSize: '0.7em',
          fontWeight: 'bold',
          color: '#831843',
          background: 'rgba(255,255,255,0.7)',
          padding: '1px',
          borderRadius: '3px',
          marginBottom: '4px'
        }}>
          ตู้ชมพู {sec === 'P1' ? 'ซ้าย' : 'ขวา'}
        </div>

        {/* 5 drawers */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
          {Array.from({ length: 5 }).map((_, rIdx) => {
            const row = rIdx + 1;
            const items = getMap2CabinetItemsList(sec, 1, row);
            const itemCount = items.length;
            const matchedSearch = cabinetHasSearchHitMap2(sec, 1, row);
            const isSelected = selectedCabinet?.map === 'map2' && selectedCabinet?.section === sec && selectedCabinet?.level === row;
            const isDropzone = !!movingItem;

            let highlightShadow = 'none';
            let highlightBorder = 'none';
            if (matchedSearch) {
              highlightBorder = '2px solid #f1c40f';
              highlightShadow = '0 0 10px rgba(241, 196, 15, 0.8)';
            } else if (isSelected) {
              highlightBorder = '2px solid #1abc9c';
              highlightShadow = '0 0 15px rgba(26, 188, 156, 0.9)';
            } else if (isDropzone) {
              highlightBorder = '1.5px dashed #3498db';
            }

            const handleClick = () => {
              if (movingItem) {
                handleQuickMoveAssignMap2(sec, 1, row);
              } else {
                setSelectedCabinet({ map: 'map2', section: sec, number: 1, level: row });
              }
            };

            return (
              <div
                key={row}
                onClick={handleClick}
                style={{
                  flex: 1,
                  background: 'linear-gradient(180deg, #fdf2f8 0%, #fbcfe8 100%)',
                  border: highlightBorder !== 'none' ? highlightBorder : '1px solid #f472b6',
                  boxShadow: highlightShadow || 'inset 0 1px 1px rgba(255,255,255,0.4)',
                  borderRadius: '3px',
                  cursor: 'pointer',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  padding: '2px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.02)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.6em', fontWeight: 'bold', color: '#db2777' }}>
                    ชั้น {row}
                  </span>
                  {itemCount > 0 && (
                    <span style={{
                      fontSize: '0.62em',
                      fontWeight: '800',
                      background: matchedSearch ? '#f1c40f' : '#1abc9c',
                      color: '#fff',
                      padding: '0px 3px',
                      borderRadius: '4px'
                    }}>
                      {itemCount}
                    </span>
                  )}
                </div>
                
                {/* Handle pull */}
                <div style={{
                  width: '25px',
                  height: '3px',
                  background: '#db2777',
                  alignSelf: 'center',
                  borderRadius: '1.5px'
                }} />
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderGreyOrganizer = () => {
    const colors = ['#2563eb', '#16a34a', '#db2777', '#ca8a04'];
    const colorsText = ['ฟ้า', 'เขียว', 'ชมพู', 'เหลือง'];
    
    return (
      <div style={{
        width: '150px',
        background: 'linear-gradient(180deg, #475569 0%, #1e293b 100%)',
        border: '3.5px solid #334155',
        borderRadius: '8px 8px 4px 4px',
        padding: '5px',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 8px 16px rgba(0,0,0,0.3)',
        height: '180px',
        justifyContent: 'space-between',
        marginBottom: '-2px',
        zIndex: 5
      }}>
        <div style={{
          textAlign: 'center',
          fontSize: '0.65em',
          fontWeight: 'bold',
          color: '#f8fafc',
          background: 'rgba(0,0,0,0.4)',
          padding: '1px',
          borderRadius: '2px',
          marginBottom: '3px'
        }}>
          📂 กล่องจัดระเบียบเทา (G1)
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', flex: 1 }}>
          {Array.from({ length: 4 }).map((_, rIdx) => {
            const row = rIdx + 1;
            const items = getMap2CabinetItemsList('G1', 1, row);
            const itemCount = items.length;
            const matchedSearch = cabinetHasSearchHitMap2('G1', 1, row);
            const isSelected = selectedCabinet?.map === 'map2' && selectedCabinet?.section === 'G1' && selectedCabinet?.level === row;
            const isDropzone = !!movingItem;

            let highlightShadow = 'none';
            let highlightBorder = 'none';
            if (matchedSearch) {
              highlightBorder = '2px solid #f1c40f';
              highlightShadow = '0 0 8px rgba(241, 196, 15, 0.8)';
            } else if (isSelected) {
              highlightBorder = '2px solid #1abc9c';
              highlightShadow = '0 0 12px rgba(26, 188, 156, 0.9)';
            } else if (isDropzone) {
              highlightBorder = '1.5px dashed #3498db';
            }

            const handleClick = () => {
              if (movingItem) {
                handleQuickMoveAssignMap2('G1', 1, row);
              } else {
                setSelectedCabinet({ map: 'map2', section: 'G1', number: 1, level: row });
              }
            };

            return (
              <div
                key={row}
                onClick={handleClick}
                style={{
                  flex: 1,
                  background: 'linear-gradient(180deg, #64748b 0%, #475569 100%)',
                  border: highlightBorder !== 'none' ? highlightBorder : '1px solid #334155',
                  boxShadow: highlightShadow || 'inset 0 1px 1px rgba(255,255,255,0.2)',
                  borderRadius: '3px',
                  cursor: 'pointer',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  padding: '2px 4px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.02)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.58em', fontWeight: 'bold', color: '#cbd5e1' }}>
                    ชั้น {row} ({colorsText[rIdx]})
                  </span>
                  {itemCount > 0 && (
                    <span style={{
                      fontSize: '0.6em',
                      fontWeight: '800',
                      background: matchedSearch ? '#f1c40f' : '#1abc9c',
                      color: '#fff',
                      padding: '0px 3px',
                      borderRadius: '3px'
                    }}>
                      {itemCount}
                    </span>
                  )}
                </div>

                <div style={{
                  width: '50px',
                  height: '5px',
                  background: colors[rIdx],
                  alignSelf: 'center',
                  borderRadius: '1px',
                  boxShadow: '0 1px 1px rgba(0,0,0,0.4)',
                  border: '0.5px solid rgba(255,255,255,0.4)'
                }} />
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderMap2 = () => {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
        {/* ROOM MAP LABEL */}
        <div style={{
          textAlign: 'center',
          fontSize: '0.85em',
          letterSpacing: '2px',
          color: '#1abc9c',
          textTransform: 'uppercase',
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }}>
          <span>🗺️</span> แผนผังตู้ติดผนังกระจกใสและชุดลิ้นชักฝั่งตรงข้าม (Wall Cabinet & Drawers)
        </div>

        {/* Outer scroll wrapper */}
        <div style={{
          overflowX: 'auto',
          width: '100%',
          paddingBottom: '15px',
          borderRadius: '12px',
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(26, 188, 156, 0.4) rgba(0,0,0,0.2)'
        }}>
          {/* Inner container with a fixed minimum width to align columns nicely */}
          <div style={{
            minWidth: '980px',
            background: 'linear-gradient(180deg, #ece3d5 0%, #dbd0c0 100%)', 
            border: '6px solid #b5a28f',
            borderRadius: '10px',
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '30px',
            boxShadow: '0 15px 30px rgba(0,0,0,0.4), inset 0 2px 10px rgba(255,255,255,0.2)'
          }}>
            {/* UPPER ROW: WALL CABINET W */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <div style={{ fontSize: '0.8em', color: '#5c4d3c', fontWeight: 'bold', borderBottom: '2px solid #cbd0c0', paddingBottom: '5px', marginBottom: '5px' }}>
                🥛 ตู้แขวนบานกระจกใส 5 ชั้น (W1 - W10)
              </div>
              <div style={{
                position: 'relative',
                background: '#b0d4de', // blue wall background
                padding: '12px',
                borderRadius: '8px',
                border: '3px solid #cbd5e0',
                display: 'flex',
                flexDirection: 'column',
                gap: '5px'
              }}>
                {/* Text overlays matching photo */}
                <div style={{
                  position: 'absolute',
                  left: '12%',
                  top: '10%',
                  background: 'rgba(255, 255, 255, 0.85)',
                  color: '#475569',
                  border: '1.5px solid #94a3b8',
                  padding: '2px 10px',
                  fontSize: '0.7em',
                  fontWeight: 'bold',
                  borderRadius: '3px',
                  zIndex: 8,
                  pointerEvents: 'none',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  หยิบจากขวาไปซ้าย
                </div>

                <div style={{
                  position: 'absolute',
                  right: '30%',
                  top: '10%',
                  background: 'rgba(255, 255, 255, 0.85)',
                  color: '#475569',
                  border: '1.5px solid #94a3b8',
                  padding: '2px 10px',
                  fontSize: '0.7em',
                  fontWeight: 'bold',
                  borderRadius: '3px',
                  zIndex: 8,
                  pointerEvents: 'none',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  หยิบจากขวาไปซ้าย
                </div>

                {/* Grid rows */}
                {Array.from({ length: 5 }).map((_, rIdx) => {
                  const rowNum = rIdx + 1;
                  return (
                    <div key={rowNum} style={{ display: 'flex', gap: '5px' }}>
                      {Array.from({ length: 10 }).map((_, cIdx) => {
                        const colNum = cIdx + 1;
                        return renderMap2WallCabinetCell(colNum, rowNum);
                      })}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* LOWER ROW: OTHER CABINET UNITS (M, P1, P2 with G1 on top) */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <div style={{ fontSize: '0.8em', color: '#5c4d3c', fontWeight: 'bold', borderBottom: '2px solid #cbd0c0', paddingBottom: '5px', marginBottom: '10px' }}>
                🪵 อุปกรณ์เก็บของฝั่งด้านล่าง (รถเข็นสแตนเลส / ตู้ลิ้นชักชมพู / ตู้กล่องเทา)
              </div>
              
              <div style={{
                display: 'flex',
                justifyContent: 'space-around',
                alignItems: 'flex-end',
                padding: '20px 10px 10px 10px',
                background: 'rgba(255,255,255,0.15)',
                borderRadius: '8px',
                minHeight: '260px'
              }}>
                {/* 1. รถเข็นสแตนเลส */}
                {renderMetalTrolley()}

                {/* 2. ตู้ลิ้นชักชมพู P1 */}
                {renderPinkCabinet('P1')}

                {/* 3. ตู้ลิ้นชักชมพู P2 + กล่องจัดระเบียบเทา G1 */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  {renderGreyOrganizer()}
                  {renderPinkCabinet('P2')}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Caption */}
        <div style={{ 
          marginTop: '12px', 
          textAlign: 'center', 
          fontSize: '0.85em', 
          color: 'rgba(255,255,255,0.5)',
          display: 'flex',
          justifyContent: 'center',
          gap: '20px'
        }}>
          <span>🥛 <strong>ตู้แขวนบานกระจก (W1 - W10):</strong> ตู้เก็บของแบบสไลด์บานกระจกใส มี 5 ชั้น</span>
          <span>⚙️ <strong>รถเข็นสแตนเลส (M1/M2):</strong> รถเข็นโลหะลิ้นชักซ้าย-ขวา ข้างละ 3 ชั้น</span>
          <span>🌸 <strong>ตู้ชมพู (P1/P2) & ตู้เทา (G1):</strong> ตู้ลิ้นชักคู่สีชมพู 5 ชั้น และกล่องตั้งหลังตู้สีเทา 4 ชั้น</span>
        </div>
      </div>
    );
  };

  const renderMap1 = () => {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {/* ROOM MAP LABEL */}
        <div style={{
          textAlign: 'center',
          fontSize: '0.85em',
          letterSpacing: '2px',
          color: '#1abc9c',
          textTransform: 'uppercase',
          marginBottom: '20px',
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }}>
          <span>🗺️</span> แผนผังตู้จัดเก็บวัสดุในห้อง Sterile (Cabinet Wall Layout)
        </div>

        {/* Outer scroll wrapper */}
        <div style={{
          overflowX: 'auto',
          width: '100%',
          paddingBottom: '15px',
          borderRadius: '12px',
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(26, 188, 156, 0.4) rgba(0,0,0,0.2)'
        }}>
          {/* Inner container with a fixed minimum width to ensure horizontal line-up of 14 cabinets */}
          <div style={{
            minWidth: '1260px',
            background: 'linear-gradient(180deg, #ece3d5 0%, #dbd0c0 100%)', 
            border: '6px solid #b5a28f', 
            borderRadius: '8px',
            padding: '12px',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 15px 30px rgba(0,0,0,0.4), inset 0 2px 10px rgba(255,255,255,0.2)'
          }}>
            {/* UPPER ROW: GLASS CABINETS (LEVEL 2) */}
            <div style={{
              display: 'flex',
              flexWrap: 'nowrap',
              gap: '5px',
              background: '#b0d4de', 
              padding: '8px 8px 12px 8px',
              borderBottom: '4px solid #ebd5c0', 
            }}>
              {Array.from({ length: 14 }, (_, idx) => renderCabinet(2, idx + 1))}
            </div>

            {/* MIDDLE ROW: COUNTERTOP PLACEMENTS (LEVEL 3) */}
            <div style={{
              display: 'flex',
              flexWrap: 'nowrap',
              gap: '5px',
              background: '#e2f1f6', 
              padding: '12px 8px 0px 8px',
              alignItems: 'flex-end', 
            }}>
              {Array.from({ length: 14 }, (_, idx) => renderCabinet(3, idx + 1))}
            </div>

            {/* COUNTERTOP WORKSPACE divider */}
            <div style={{
              height: '18px',
              background: 'linear-gradient(180deg, #24252a 0%, #0d0d0f 100%)',
              borderTop: '2px solid #5a5f6e',
              borderBottom: '2px solid #000',
              boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
              position: 'relative',
              zIndex: 5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <div style={{
                width: '80%',
                height: '1px',
                background: 'rgba(255,255,255,0.06)'
              }} />
            </div>

            {/* LOWER ROW: WOOD CABINETS (LEVEL 1) */}
            <div style={{
              display: 'flex',
              flexWrap: 'nowrap',
              gap: '5px',
              padding: '12px 8px 8px 8px',
              background: '#ecdcc8'
            }}>
              {Array.from({ length: 14 }, (_, idx) => renderCabinet(1, idx + 1))}
            </div>
          </div>
        </div>
        
        {/* Caption */}
        <div style={{ 
          marginTop: '12px', 
          textAlign: 'center', 
          fontSize: '0.85em', 
          color: 'rgba(255,255,255,0.5)',
          display: 'flex',
          justifyContent: 'center',
          gap: '20px'
        }}>
          <span>🥛 <strong>แถวบน (ตู้ B1 - B14):</strong> ตู้แขวนบานกระจกใส</span>
          <span>🪵 <strong>แถวล่าง (ตู้ A1 - A14):</strong> ตู้เคาน์เตอร์ไม้ (ตู้ลิ้นชัก/ตู้บานเปิดคู่/ช่องโล่ง)</span>
        </div>
      </div>
    );
  };

  return (
    <div style={{
      background: '#111827',
      borderRadius: '24px',
      border: '1px solid rgba(255,255,255,0.08)',
      padding: '30px',
      margin: '0 auto 30px auto',
      color: '#ffffff',
      fontFamily: "'Segoe UI', 'Noto Sans Thai', -apple-system, sans-serif",
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
    }}>
      {/* HEADER SECTION */}
      <div style={{ textAlign: 'center', marginBottom: '30px' }}>
        <h2 style={{ fontSize: '1.8em', fontWeight: 'bold', color: '#1abc9c', margin: '0 0 8px 0', textShadow: '0 2px 10px rgba(0,0,0,0.3)' }}>
          📍 แผนผังพิกัดสิ่งของในห้อง Sterile
        </h2>
        <p style={{ color: 'rgba(255,255,255,0.6)', margin: 0, fontSize: '0.95em' }}>
          แสดงชั้นเก็บของและตู้จัดเก็บเครื่องมือต่างๆ ในคลังเก็บอุปกรณ์ปราศจากเชื้อ (Sterile Room)
        </p>
      </div>

      {/* QUICK PLACEMENT BANNER */}
      {movingItem && (
        <div style={{
          background: 'rgba(52, 152, 219, 0.15)',
          border: '1px dashed #3498db',
          borderRadius: '12px',
          padding: '12px 20px',
          marginBottom: '20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          animation: 'pulse 2s infinite',
          zIndex: 999
        }}>
          <div style={{ fontSize: '0.95em', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '1.2em' }}>🎯</span>
            <span>กำลังย้ายตำแหน่ง: <strong>{movingItem.item_name}</strong> - กรุณาคลิกเลือกตู้ปลายทางบนแผนผัง (สลับแท็บแผนผังได้ตามต้องการ)</span>
          </div>
          <button 
            onClick={() => setMovingItem(null)} 
            style={{
              background: '#e74c3c',
              border: 'none',
              color: '#fff',
              borderRadius: '6px',
              padding: '4px 10px',
              cursor: 'pointer',
              fontSize: '0.85em',
              fontWeight: 'bold'
            }}
          >
            ยกเลิก
          </button>
        </div>
      )}

      {/* SEARCH AND QUICK BAR */}
      <div style={{
        display: 'flex',
        gap: '15px',
        marginBottom: '25px',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ flex: '1 1 300px', position: 'relative' }}>
          <input
            type="text"
            placeholder="🔍 ค้นหาชื่อวัสดุ / ตู้ / ป้ายชื่ออื่นๆ ที่จัดเก็บ..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px 12px 40px',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '12px',
              color: '#fff',
              fontSize: '0.95em',
              transition: 'all 0.3s'
            }}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                color: 'rgba(255,255,255,0.4)',
                cursor: 'pointer',
                fontSize: '1.1em'
              }}
            >
              ✕
            </button>
          )}
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <div style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.5)', display: 'flex', alignItems: 'center', gap: '5px' }}>
            <span style={{ display: 'inline-block', width: '12px', height: '12px', background: '#2ecc71', borderRadius: '3px' }} /> น้อย
            <span style={{ display: 'inline-block', width: '12px', height: '12px', background: '#3498db', borderRadius: '3px' }} /> ปานกลาง
            <span style={{ display: 'inline-block', width: '12px', height: '12px', background: '#9b59b6', borderRadius: '3px' }} /> หนาแน่น
          </div>
        </div>
      </div>

      {/* MAP SELECTOR TABS */}
      <div style={{
        display: 'flex',
        gap: '10px',
        marginBottom: '20px',
        borderBottom: '2px solid rgba(255,255,255,0.08)',
        paddingBottom: '10px'
      }}>
        <button
          onClick={() => {
            setActiveMap('map1');
            setSelectedCabinet(null);
          }}
          style={{
            padding: '10px 20px',
            background: activeMap === 'map1' ? 'linear-gradient(135deg, #1abc9c 0%, #16a085 100%)' : 'rgba(255,255,255,0.05)',
            border: 'none',
            borderRadius: '8px',
            color: '#fff',
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: 'all 0.2s',
            boxShadow: activeMap === 'map1' ? '0 4px 12px rgba(26, 188, 156, 0.3)' : 'none'
          }}
        >
          🗄️ แผนผัง 1: เคาน์เตอร์ฝั่งหลัก (A, B, C)
        </button>
        <button
          onClick={() => {
            setActiveMap('map2');
            setSelectedCabinet(null);
          }}
          style={{
            padding: '10px 20px',
            background: activeMap === 'map2' ? 'linear-gradient(135deg, #1abc9c 0%, #16a085 100%)' : 'rgba(255,255,255,0.05)',
            border: 'none',
            borderRadius: '8px',
            color: '#fff',
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: 'all 0.2s',
            boxShadow: activeMap === 'map2' ? '0 4px 12px rgba(26, 188, 156, 0.3)' : 'none'
          }}
        >
          🚪 แผนผัง 2: ตู้กระจก & ลิ้นชักตรงข้าม (W, M, P, G)
        </button>
      </div>

      {/* GRAPHICAL STORAGE PLAN */}
      <div style={{
        background: '#1f2937',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '20px',
        padding: '25px',
        marginBottom: '30px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.3)'
      }}>
        {activeMap === 'map1' ? renderMap1() : renderMap2()}
      </div>

      {/* DETAILED CONTENT SECTION */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 320px',
        gap: '25px',
        alignItems: 'start',
        flexWrap: 'wrap'
      }} className="sterile-locations-details">
        
        {/* LEFT COLUMN: SELECTED CABINET ITEMS TABLE */}
        <div style={{
          background: 'rgba(255,255,255,0.02)',
          border: '1px solid rgba(255,255,255,0.05)',
          borderRadius: '16px',
          padding: '24px',
          minHeight: '350px'
        }}>
          {selectedCabinet ? (
            <div>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '20px',
                borderBottom: '1px solid rgba(255,255,255,0.08)',
                paddingBottom: '12px'
              }}>
                <h3 style={{ margin: 0, fontSize: '1.2em', color: '#1abc9c', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  📦 ของในตู้นี้: {
                    selectedCabinet.map === 'map2' 
                      ? (
                        selectedCabinet.section === 'W' ? `ตู้กระจกติดผนัง W${selectedCabinet.number} ชั้น ${selectedCabinet.level}` :
                        selectedCabinet.section === 'M' ? `รถเข็นสแตนเลส (ลิ้นชักฝั่ง${selectedCabinet.number === 1 ? 'ซ้าย' : 'ขวา'} ชั้น ${selectedCabinet.level})` :
                        selectedCabinet.section === 'P1' ? `ตู้ชมพูซ้าย ลิ้นชักชั้น ${selectedCabinet.level}` :
                        selectedCabinet.section === 'P2' ? `ตู้ชมพูขวา ลิ้นชักชั้น ${selectedCabinet.level}` :
                        selectedCabinet.section === 'G1' ? `ตู้กล่องเทา ชั้น ${selectedCabinet.level}` : ''
                      )
                      : `ตู้ ${selectedCabinet.level === 1 ? 'A' : (selectedCabinet.level === 2 ? 'B' : 'C')}${selectedCabinet.number} (${selectedCabinet.level === 3 ? 'เคาน์เตอร์ตรงกลาง' : `ชั้นที่ ${selectedCabinet.level}`})`
                  }
                </h3>
                <span style={{
                  background: 'rgba(26,188,156,0.15)',
                  color: '#1abc9c',
                  padding: '2px 8px',
                  borderRadius: '10px',
                  fontSize: '0.85em',
                  fontWeight: 'bold'
                }}>
                  ทั้งหมด {activeCabinetItems.length} รายการ
                </span>
              </div>

              {activeCabinetItems.length === 0 ? (
                <div style={{
                  padding: '60px 0',
                  textAlign: 'center',
                  color: 'rgba(255,255,255,0.3)',
                  fontSize: '0.9em'
                }}>
                  <span style={{ fontSize: '2em', display: 'block', marginBottom: '10px' }}>📦</span>
                  ตู้เก็บของว่าง หรือ ไม่มีข้อมูลของที่ตรงกับเงื่อนไขการค้นหา
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.9em' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.5)' }}>
                        <th style={{ padding: '10px' }}>รูปภาพ</th>
                        <th style={{ padding: '10px' }}>ชื่ออุปกรณ์คงทน</th>
                        <th style={{ padding: '10px' }}>พิกัดย่อย (ชั้น/แถว)</th>
                        <th style={{ padding: '10px', textAlign: 'center' }}>จำนวนสต็อก</th>
                        <th style={{ padding: '10px', textAlign: 'center' }}>จัดการ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeCabinetItems.map((item, idx) => {
                        const countInSterile = getSterileStock(item.id);
                        return (
                          <tr 
                            key={item.id} 
                            style={{ 
                              borderBottom: '1px solid rgba(255,255,255,0.05)',
                              background: idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)'
                            }}
                          >
                            <td style={{ padding: '10px' }}>
                              <div style={{
                                width: '45px',
                                height: '45px',
                                borderRadius: '6px',
                                overflow: 'hidden',
                                background: 'rgba(0,0,0,0.2)',
                                border: '1px solid rgba(255,255,255,0.1)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                {item.image_url ? (
                                  <img src={item.image_url.split('|')[0]} alt={item.item_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                  <span style={{ fontSize: '1.1em' }}>🏢</span>
                                )}
                              </div>
                            </td>
                            <td style={{ padding: '10px' }}>
                              <div style={{ fontWeight: 'bold', color: '#ffffff' }}>{item.item_name}</div>
                              {item.aliases && (
                                <div style={{ fontSize: '0.8em', color: 'rgba(255,255,255,0.4)', marginTop: '2px' }}>
                                  ชื่อย่อ: {item.aliases}
                                </div>
                              )}
                            </td>
                            <td style={{ padding: '10px' }}>
                              <div style={{ fontSize: '0.9em', color: '#1abc9c', fontWeight: 'bold' }}>
                                {item.shelf ? `ชั้น: ${item.shelf}` : ''} {item.row ? `| แถว: ${item.row}` : ''}
                              </div>
                              <span style={{ fontSize: '0.78em', color: 'rgba(255,255,255,0.4)' }}>
                                {item.cabinet || (selectedCabinet.map === 'map2' 
                                  ? `${selectedCabinet.section}${selectedCabinet.number === 1 && ['P1','P2','G1'].includes(selectedCabinet.section) ? '' : selectedCabinet.number}`
                                  : `ตู้ ${selectedCabinet.level === 1 ? 'A' : 'B'}${selectedCabinet.number}`
                                )}
                              </span>
                            </td>
                            <td style={{ padding: '10px', textAlign: 'center' }}>
                              <span style={{
                                background: countInSterile > 0 ? 'rgba(46, 204, 113, 0.15)' : 'rgba(231, 76, 60, 0.15)',
                                color: countInSterile > 0 ? '#2ecc71' : '#e74c3c',
                                padding: '3px 10px',
                                borderRadius: '20px',
                                fontWeight: 'bold',
                                fontSize: '0.9em'
                              }}>
                                {countInSterile} {item.unit || 'ชิ้น'}
                              </span>
                            </td>
                            <td style={{ padding: '10px', textAlign: 'center' }}>
                              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                <button
                                  onClick={() => openEditModal(item)}
                                  style={{
                                    background: 'rgba(255,255,255,0.06)',
                                    border: '1px solid rgba(255,255,255,0.1)',
                                    color: '#fff',
                                    padding: '5px 10px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '0.82em',
                                    fontWeight: 'bold',
                                    transition: 'all 0.2s'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}
                                  onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
                                >
                                  แก้ไขพิกัด
                                </button>
                                <button
                                  onClick={() => setMovingItem(item)}
                                  style={{
                                    background: 'rgba(52, 152, 219, 0.15)',
                                    border: '1px solid rgba(52, 152, 219, 0.3)',
                                    color: '#3498db',
                                    padding: '5px 10px',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '0.82em',
                                    fontWeight: 'bold',
                                    transition: 'all 0.2s'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = 'rgba(52, 152, 219, 0.25)';
                                    e.currentTarget.style.color = '#fff';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'rgba(52, 152, 219, 0.15)';
                                    e.currentTarget.style.color = '#3498db';
                                  }}
                                >
                                  ย้ายตู้ด่วน
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '300px',
              color: 'rgba(255,255,255,0.4)',
              textAlign: 'center'
            }}>
              <span style={{ fontSize: '3em', marginBottom: '15px' }}>👈</span>
              <div style={{ fontWeight: 'bold', fontSize: '1.1em', marginBottom: '5px' }}>ยังไม่ได้เลือกตู้เก็บของ</div>
              <div>{activeMap === 'map2' ? 'กรุณาคลิกเลือกช่องเก็บของตู้กระจก W1-W10, รถเข็น M1-M2, ตู้ชมพู P1-P2 หรือกล่องเทา G1 บนแผนผังเพื่อดูรายการอุปกรณ์' : 'กรุณาคลิกเลือก ตู้เก็บของ A1-A14 หรือ B1-B14 บนแผนผังเพื่อดูรายการอุปกรณ์ที่จัดเก็บอยู่ภายใน'}</div>
            </div>
          )}
        </div>

        {/* RIGHT COLUMN: UNMAPPED / ALL DURABLE ITEMS PANEL */}
        <div style={{
          background: 'rgba(255,255,255,0.02)',
          border: '1px solid rgba(255,255,255,0.05)',
          borderRadius: '16px',
          padding: '20px'
        }}>
          <h3 style={{ margin: '0 0 15px 0', fontSize: '1.05em', color: '#1abc9c', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: '10px' }}>
            ⚠️ วัสดุคงทนที่ยังไม่กำหนดตำแหน่ง
          </h3>
          
          {unassignedItems.length === 0 ? (
            <div style={{
              padding: '20px',
              textAlign: 'center',
              color: 'rgba(46, 204, 113, 0.6)',
              fontSize: '0.85em',
              background: 'rgba(46, 204, 113, 0.05)',
              borderRadius: '10px'
            }}>
              ✓ จัดเก็บอุปกรณ์คงทนครบถ้วนหมดแล้ว
            </div>
          ) : (
            <div style={{ maxHeight: '400px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ fontSize: '0.8em', color: 'rgba(255,255,255,0.4)', marginBottom: '5px' }}>
                มีพัสดุ {unassignedItems.length} ชิ้นไม่มีข้อมูล ตู้/ชั้น/แถว:
              </div>
              {unassignedItems.map(item => (
                <div 
                  key={item.id}
                  style={{
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.06)',
                    borderRadius: '10px',
                    padding: '10px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px'
                  }}
                >
                  <div style={{ fontSize: '0.88em', fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {item.item_name}
                  </div>
                  <div style={{ display: 'flex', gap: '5px', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.75em', color: 'rgba(255,255,255,0.4)' }}>
                      คลัง: {getSterileStock(item.id)} {item.unit || 'ชิ้น'}
                    </span>
                    <button
                      onClick={() => setMovingItem(item)}
                      style={{
                        background: '#3498db',
                        border: 'none',
                        borderRadius: '4px',
                        color: '#fff',
                        padding: '3px 8px',
                        fontSize: '0.75em',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                      }}
                    >
                      จัดเก็บด่วน
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* EDIT MODAL FOR LOCATION COORDINATES */}
      {editItem && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.85)',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            background: '#1f2937',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '20px',
            padding: '30px',
            width: '100%',
            maxWidth: '450px',
            color: '#fff',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7)'
          }}>
            <h3 style={{ margin: '0 0 15px 0', color: '#1abc9c', fontSize: '1.25em', display: 'flex', alignItems: 'center', gap: '8px' }}>
              ✏️ แก้ไขพิกัดจัดเก็บเครื่องมือ
            </h3>
            
            <div style={{ 
              background: 'rgba(255,255,255,0.03)', 
              borderRadius: '8px', 
              padding: '10px 15px', 
              marginBottom: '20px',
              fontSize: '0.9em',
              borderLeft: '4px solid #1abc9c'
            }}>
              <strong>อุปกรณ์:</strong> {editItem.item_name}
            </div>

            <form onSubmit={handleSaveLocation}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginBottom: '25px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <label style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.6)' }}>ตู้เก็บของ</label>
                  <input
                    type="text"
                    value={editCab}
                    onChange={(e) => setEditCab(e.target.value)}
                    placeholder="เช่น ตู้ A1, ตู้ B5, ตู้ W3"
                    required
                    style={{
                      padding: '10px 12px',
                      background: 'rgba(0,0,0,0.2)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <label style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.6)' }}>ชั้นเก็บของ (ในตู้นั้น)</label>
                  <input
                    type="text"
                    value={editShelf}
                    onChange={(e) => setEditShelf(e.target.value)}
                    placeholder="เช่น ชั้น 1, ชั้น 2, ชั้น 3"
                    style={{
                      padding: '10px 12px',
                      background: 'rgba(0,0,0,0.2)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <label style={{ fontSize: '0.85em', color: 'rgba(255,255,255,0.6)' }}>แถว / ช่องจัดเก็บ</label>
                  <input
                    type="text"
                    value={editRow}
                    onChange={(e) => setEditRow(e.target.value)}
                    placeholder="เช่น แถว 1, แถว 2, ช่องซ้าย"
                    style={{
                      padding: '10px 12px',
                      background: 'rgba(0,0,0,0.2)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setEditItem(null)}
                  disabled={isSaving}
                  style={{
                    padding: '8px 16px',
                    background: 'transparent',
                    border: '1px solid rgba(255,255,255,0.15)',
                    color: '#fff',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  ยกเลิก
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  style={{
                    padding: '8px 20px',
                    background: '#1abc9c',
                    border: 'none',
                    color: '#fff',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    boxShadow: '0 4px 10px rgba(26, 188, 156, 0.3)'
                  }}
                >
                  {isSaving ? 'กำลังบันทึก...' : '💾 บันทึกตำแหน่ง'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SterileLocations;
